import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/shared/types';
import { createApi } from '@reduxjs/toolkit/query/react';
import { ComplianceNoteType } from '../enum/compliance-note.enum';
import { CreateComplianceNote } from '../types/create-compliance-note.type';

export const complianceNoteApi = createApi({
  reducerPath: 'complianceNoteApi',
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    generateComplianceNote: builder.mutation<
      ApiResponse<any>,
      { userContent: string; noteCategory: ComplianceNoteType }
    >({
      query: (body) => ({
        url: 'v1/compliance-note/generate',
        method: 'POST',
        body,
      }),
    }),
    saveComplianceNote: builder.mutation<
      ApiResponse<any>,
      { payload: CreateComplianceNote; assessmentId?: string }
    >({
      query: ({ payload, assessmentId }) => ({
        url: 'v1/compliance-note/save',
        method: 'POST',
        body: payload,
        // Backend links the saved note to the assessment when this is present.
        ...(assessmentId ? { params: { assessmentId } } : {}),
      }),
    }),

    getComplianceNote: builder.query<
      ApiResponse<any>,
      { serviceName: string; serviceId: string }
    >({
      query: (objectType) =>
        `v1/compliance-note/find/by-service?serviceName=${objectType.serviceName}&serviceId=${objectType.serviceId}`,
    }),
    getComplianceNoteList: builder.query<
      ApiResponse<any>,
      { page: number; size?: number; searchParam?: string }
    >({
      query: ({ page, size = 10, searchParam }) => {
        return {
          url: 'v1/compliance-note',
          method: 'GET',
          params: {
            page,
            size,
            ...(searchParam && { searchParam }),
          },
        };
      },
    }),
    updateComplianceNote: builder.mutation<
      ApiResponse<any>,
      { id: string; payload: Partial<CreateComplianceNote> }
    >({
      query: (body) => ({
        url: `v1/compliance-note/update/${body.id}`,
        method: 'PATCH',
        body: body.payload,
      }),
    }),
    getCompliancePromptByType: builder.query<
      ApiResponse<any>,
      { type: ComplianceNoteType }
    >({
      query: ({ type }) => `v1/compliance-note/prompt/${type}`,
    }),
    updateCompliancePromptByType: builder.mutation<
      ApiResponse<any>,
      { type: ComplianceNoteType; prompt: string }
    >({
      query: ({ type, prompt }) => ({
        url: `v1/compliance-note/prompt/${type}`,
        method: 'PATCH',
        body: { prompt },
      }),
    }),
  }),
});

export const {
  useGenerateComplianceNoteMutation,
  useSaveComplianceNoteMutation,
  useUpdateComplianceNoteMutation,
  useGetComplianceNoteQuery,
  useGetComplianceNoteListQuery,
  useUpdateCompliancePromptByTypeMutation,
  useLazyGetCompliancePromptByTypeQuery,
} = complianceNoteApi;
